import { useState } from "react";
import { ChevronDown } from "lucide-react";

export const CustomerJourney = ({ 
  platformBreakdown, 
  marketingChannels, 
  deviceMetrics,
  mobileSummary,
  socialMirrorSummary,
  onOpenChannelGuide,
  onOpenPPCModal 
}) => {
  const [openStage, setOpenStage] = useState(null);

  const toggle = (idx) => setOpenStage(openStage === idx ? null : idx);

  const activeChannels = new Set();
  platformBreakdown.forEach(p => {
    if (p.impressions > 0) activeChannels.add(p.platform.toLowerCase());
  });
  marketingChannels.filter(c => c.is_active).forEach(c => {
    activeChannels.add(c.channel_type.toLowerCase());
  });
  if (socialMirrorSummary?.overview?.total_impressions > 0) {
    activeChannels.add('social_mirror');
  }

  const hasMobile = deviceMetrics.some(d => d.device_name === 'Mobile' && d.impressions > 0) || (mobileSummary?.overview?.total_impressions > 0);

  const channelInfo = {
    ctv: { icon: '📺', name: 'SmartTV Ads (aka CTV, OTT)' },
    local_tv: { icon: '📡', name: 'Local TV' },
    radio: { icon: '📻', name: 'Internet Radio Ads' },
    local_radio: { icon: '🎙️', name: 'Local Radio' },
    billboard: { icon: '🪧', name: 'Billboard' },
    print: { icon: '📰', name: 'Print' },
    tiktok: { icon: '🎵', name: 'TikTok' },
    podcast: { icon: '🎧', name: 'Podcast' },
    email: { icon: '✉️', name: 'Email' },
    mailings: { icon: '📬', name: 'Direct Mail' },
    organic_social: { icon: '🌐', name: 'Organic Social Posts' },
    loyalty_app: { icon: '⭐', name: 'Loyalty App Programs' },
    facebook: { icon: '📘', name: 'Paid Facebook Ads' },
    instagram: { icon: '📸', name: 'Paid Instagram Ads' },
    social_mirror: { icon: '🪞', name: 'Social Mirror Ads' },
    google: { icon: '🔍', name: 'Google Ads' },
    retargeting: { icon: '🎯', name: 'Retargeting' },
    search: { icon: '🔎', name: 'Search' }
  };

  const searchPlatformLogos = [
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/2ogzqkim_facebook.png", alt: "Facebook" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/e19ae11s_every-social-media-logo-and-icon-in-one-handy-place-instagram-app-logo.webp", alt: "Instagram" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/8i1w20dn_youtube.png", alt: "YouTube" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/v1owe0rg_LinkedIn_logo_initials.png", alt: "LinkedIn" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/7d29huqk_Untitled%20%282%29.png", alt: "Google Business" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/tr2kocn1_google%20maps%202026.png", alt: "Google Maps" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/bkzdas1g_apple_maps_icon-vector_brandlogos.net_ppkzj.png", alt: "Apple Maps" },
    { src: "https://customer-assets.emergentagent.com/job_43da4cb5-a802-476c-b2c7-0070e1f27e21/artifacts/bqyay1eg_sq-google-g-logo-update_dezeen_2364_col_0.jpg", alt: "Google" },
  ];

  const stages = [
    {
      emoji: '🌱',
      title: 'Planting Seeds',
      marketingTerm: 'Brand Awareness',
      bigIdea: 'They don\'t need you yet. But they will.',
      story: 'This is your REACH medium: the top of the funnel where you cast the widest net. Your future customer is watching TV, scrolling their phone, driving to work. They see your name. They hear your jingle. Why does reach matter? Because you can\'t convert someone who\'s never heard of you. The bigger your reach, the larger your pool of future customers.',
      example: '"I\'ve seen that company before..."',
      color: '#8b5cf6',
      channels: ['ctv', 'local_tv', 'radio', 'local_radio', 'billboard', 'print', 'tiktok', 'podcast', 'social_mirror'],
      dualChannels: ['facebook', 'instagram'],
      mobileNote: hasMobile ? '📱 Mobile Ads' : null
    },
    {
      emoji: '🤝',
      title: 'Building Trust',
      marketingTerm: 'Consideration & Nurturing',
      bigIdea: 'Now you\'re building the relationship.',
      story: 'They\'ve seen you around. Now it\'s time to nurture that awareness into trust. Through consistent messaging, educational content, and staying top-of-mind, you\'re moving them from "I\'ve heard of them" to "I trust them." This is where email sequences, social engagement, and retargeting work their magic.',
      example: '"I keep seeing them everywhere... they must be good."',
      color: '#10b981',
      channels: ['email', 'mailings', 'organic_social', 'loyalty_app'],
      dualChannels: ['facebook', 'instagram'],
      mobileNote: hasMobile ? '📱 Mobile Retargeting' : null
    },
    {
      emoji: '🎯',
      title: 'The Moment of Truth',
      marketingTerm: 'Conversion & Purchase Intent',
      bigIdea: 'They need you NOW.',
      story: 'The roof starts leaking. The AC dies in July. They grab their phone and search: on Google, Facebook, Instagram, or wherever they spend time. This is the moment everything you\'ve built leads to. And here\'s what matters: when they find you, do you look the same everywhere? Consistency builds trust. If your hours are wrong on Google, your logo is outdated on Facebook, or your phone number is missing on Instagram. That\'s friction. And friction kills conversions.',
      example: '"I remember seeing their ads: let me call them."',
      color: '#f59e0b',
      channels: ['google', 'retargeting', 'search'],
      dualChannels: ['facebook', 'instagram'],
      mobileNote: null,
      alwaysShow: true
    }
  ];

  return (
    <div className="bg-white/5 rounded-xl p-6 border border-white/10 mb-6" data-testid="customer-journey">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-xl text-white font-bold">How People Become Your Customers</p>
          <p className="text-slate-500 text-sm mt-0.5">First they notice you, then they trust you, then they choose you.</p>
        </div>
        <button
          onClick={onOpenChannelGuide}
          className="w-7 h-7 rounded-full bg-violet-500/20 hover:bg-violet-500/30 flex items-center justify-center transition-colors border border-violet-500/40 flex-shrink-0"
          title="Channel Guide"
          data-testid="channel-guide-btn"
        >
          <svg className="w-4 h-4 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>

      {/* Three columns — each with its own inline dropdown */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
        {stages.map((stage, idx) => {
          const isOpen = openStage === idx;
          const primaryChannels = stage.channels.filter(ch => activeChannels.has(ch));
          const dualCh = stage.dualChannels.filter(ch => activeChannels.has(ch));
          const allStageChannels = [...primaryChannels, ...dualCh];

          return (
            <div 
              key={idx} 
              className="rounded-xl overflow-hidden transition-all"
              style={{ border: `1.5px solid ${isOpen ? stage.color + '40' : 'rgba(255,255,255,0.08)'}` }}
            >
              {/* Column header / trigger */}
              <button
                onClick={() => toggle(idx)}
                data-testid={`journey-stage-${idx}`}
                className="w-full flex flex-col items-center text-center px-5 py-6 transition-colors hover:bg-white/[0.04] cursor-pointer"
                style={isOpen ? { backgroundColor: `${stage.color}10` } : {}}
              >
                <span className="text-4xl mb-2">{stage.emoji}</span>
                <p className="font-bold text-white text-xl">{stage.title}</p>
                <span 
                  className="text-xs px-3 py-1 rounded-full font-semibold mt-2"
                  style={{ backgroundColor: `${stage.color}20`, color: stage.color }}
                >
                  {stage.marketingTerm}
                </span>
                <p className="text-base mt-2.5 font-medium text-slate-300">{stage.bigIdea}</p>
                <span 
                  className="mt-3 text-xs font-semibold flex items-center gap-1 transition-colors"
                  style={{ color: stage.color }}
                >
                  {isOpen ? 'Close' : 'Learn More'}
                  <ChevronDown 
                    className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>

              {/* Inline dropdown — stays within the column */}
              {isOpen && (
                <div className="px-4 pb-4 pt-1 border-t" style={{ backgroundColor: `${stage.color}05`, borderColor: `${stage.color}20` }}>
                  <p className="text-slate-300 text-sm leading-relaxed mb-3">
                    {stage.story}
                  </p>
                  <div className="inline-flex items-center gap-2 bg-white/5 rounded-full px-3 py-1.5 border border-white/10 mb-3">
                    <span className="text-sm">💭</span>
                    <span className="text-slate-400 italic text-xs">{stage.example}</span>
                  </div>

                  {/* Channels */}
                  {stage.title === 'The Moment of Truth' ? (
                    <div className="mt-2">
                      <p className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: stage.color }}>
                        Where They'll Search
                      </p>
                      <div className="grid grid-cols-4 gap-1.5">
                        {searchPlatformLogos.map((logo) => (
                          <div key={logo.alt} className="flex items-center justify-center p-1.5 bg-white/5 rounded-lg">
                            <img src={logo.src} alt={logo.alt} className="w-7 h-7 object-contain rounded" />
                          </div>
                        ))}
                      </div>
                      {/* Brand Consistency CTA */}
                      <a 
                        href="https://brand-report-r-1773343747.emergent.host/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 flex items-center gap-2.5 px-3 py-2.5 rounded-xl transition-all hover:scale-[1.02] bg-amber-500/15 border border-amber-500/40"
                        data-testid="brand-consistency-cta"
                      >
                        <div className="w-8 h-8 rounded-lg bg-amber-500/30 flex items-center justify-center flex-shrink-0">
                          <svg className="w-4 h-4 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white text-xs">Brand Consistency Check</p>
                          <p className="text-[11px] text-slate-400">Free 2-min checkup</p>
                        </div>
                        <svg className="w-3.5 h-3.5 text-slate-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  ) : (
                    <div 
                      className="rounded-xl p-3 border mt-2"
                      style={{ backgroundColor: `${stage.color}10`, borderColor: `${stage.color}30` }}
                    >
                      <p className="text-xs uppercase tracking-wider mb-2 font-semibold" style={{ color: stage.color }}>
                        Your Channels Here
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {allStageChannels.map(ch => {
                          const info = channelInfo[ch] || { icon: '📊', name: ch };
                          return (
                            <span key={ch} className="text-xs px-2 py-1 rounded-lg flex items-center gap-1 bg-white/10 border border-white/10">
                              <span>{info.icon}</span>
                              <span className="text-white font-medium">{info.name}</span>
                            </span>
                          );
                        })}
                        {stage.mobileNote && (
                          <span className="text-xs px-2 py-1 rounded-lg flex items-center gap-1 bg-white/10 border border-white/10">
                            <span className="text-white font-medium">{stage.mobileNote}</span>
                          </span>
                        )}
                        {allStageChannels.length === 0 && !stage.mobileNote && (
                          <span className="text-slate-500 text-xs italic">None active</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
